import React, { useCallback, useRef } from 'react'

export interface TabConfig {
  id: string
  label: string
  icon: string
}

interface TabNavigationProps {
  tabs: TabConfig[]
  activeTab: string
  onTabChange: (tabId: string) => void
}

/**
 * Accessible tab navigation with keyboard support
 *
 * Implements ARIA tablist semantics with arrow key navigation.
 * Light theme styling with active indicator.
 */
export const TabNavigation: React.FC<TabNavigationProps> = ({
  tabs,
  activeTab,
  onTabChange
}) => {
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  // Focus management for keyboard navigation
  const focusTab = useCallback((tabId: string) => {
    const tabElement = tabRefs.current.get(tabId)
    if (tabElement) {
      tabElement.focus()
    }
  }, [])

  // Handle keyboard navigation (Arrow keys)
  const handleKeyDown = useCallback((e: React.KeyboardEvent, currentIndex: number) => {
    let nextIndex = currentIndex

    if (e.key === 'ArrowRight') {
      e.preventDefault()
      nextIndex = (currentIndex + 1) % tabs.length
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      nextIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1
    } else {
      return
    }

    const nextTab = tabs[nextIndex]
    if (nextTab) {
      onTabChange(nextTab.id)
      focusTab(nextTab.id)
    }
  }, [tabs, onTabChange, focusTab])

  return (
    <div
      role="tablist"
      className="flex overflow-x-auto border-b border-gray-200 bg-white flex-shrink-0"
    >
      {tabs.map((tab, index) => {
        const isActive = activeTab === tab.id
        return (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) {
                tabRefs.current.set(tab.id, el)
              } else {
                tabRefs.current.delete(tab.id)
              }
            }}
            role="tab"
            aria-selected={isActive}
            aria-controls={`${tab.id}-panel`}
            id={`${tab.id}-tab`}
            tabIndex={isActive ? 0 : -1}
            data-testid={`settings-tab-${tab.id}`}
            data-tour={`tab-${tab.id}`}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
              isActive
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="mr-1">{tab.icon}</span>
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
